import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './Header.css';
import { getInsights } from './services/api';
import { useLocalizedContent } from './hooks/useLocalizedContent';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';

const Header = () => {
  const { t } = useTranslation();
  const { getLocalized } = useLocalizedContent();
  
  // 状态管理
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);

  // 从API获取数据 - 支持请求取消
  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 获取市场研究类别的文章，用于轮播和文章列表
        const marketResearchData = await getInsights(
          { category: 'market-research', limit: 5 },
          abortController.signal
        );
        
        // 将获取的市场研究文章转换为 carousel 和 articles 格式
        const insights = marketResearchData || [];
        
        // 构建轮播数据：使用文章的封面图和标题
        const carouselData = insights.map(insight => ({
          id: insight._id || insight.id,
          image: insight.cover_image || '/placeholder-image.jpg',
          title: insight.title,
          link: `/insights/${insight.slug}`
        }));
        
        // 构建文章列表数据
        const articlesData = insights.map(insight => ({
          id: insight._id || insight.id,
          title: insight.title
        }));
        
        setCarouselSlides(carouselData);
        setArticles(articlesData);
        setLoading(false);
      } catch (err) {
        // 忽略请求取消错误
        if (err.code === 'ERR_CANCELED' || err.message === 'canceled' || err.name === 'CanceledError') {
          return;
        }
        console.error('Failed to fetch header data:', err);
        setError(err.message || '加载数据失败');
        setLoading(false);
      }
    };

    fetchData();
    
    return () => {
      abortController.abort();
    };
  }, []);

  // 重试加载函数
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    window.location.reload();
  }, []);

  // 自动轮播
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
      setProgress(0);
    }, 5000);

    return () => clearInterval(slideInterval);
  }, [carouselSlides.length]);

  // 进度条动画
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 1));
    }, 50);

    return () => clearInterval(progressInterval);
  }, []);

  // 手动切换幻灯片
  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setProgress(0);
  }, []);

  // 使用useMemo缓存处理后的数据
  const localizedCarouselSlides = useMemo(() => {
    return carouselSlides.map(slide => ({
      ...slide,
      title: getLocalized(slide.title)
    }));
  }, [carouselSlides, getLocalized]);

  const localizedArticles = useMemo(() => {
    return articles.map(article => ({
      ...article,
      title: getLocalized(article.title)
    }));
  }, [articles, getLocalized]);

  // 显示加载状态
  if (loading) {
    return (
      <header className="app-header-container">
        <div className="carousel-container">
          <div className="carousel-slide active" style={{ backgroundColor: '#f0f0f0', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LoadingSpinner size="large" />
          </div>
        </div>
      </header>
    );
  }

  // 显示错误状态
  if (error) {
    return (
      <header className="app-header-container">
        <div className="carousel-container" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorMessage message={error} onRetry={handleRetry} />
        </div>
      </header>
    );
  }

  return (
    <header className="app-header-container">
      <div className="carousel-container">
        {localizedCarouselSlides.map((slide, index) => (
          <div 
            key={slide.id}
            className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
            onClick={() => window.location.href = slide.link}
            role="button"
            tabIndex={0}
            aria-label={slide.title}
          >
            <div className="slide-content">
              <h1 className="slide-title">{slide.title}</h1>
            </div>
          </div>
        ))}
      </div>
      
      <div className="articles-list">
        {localizedArticles.map((article, index) => (
          <div 
            key={article.id} 
            className="article-item"
            onClick={() => goToSlide(index % localizedCarouselSlides.length)}
            role="button"
            tabIndex={0}
            aria-label={article.title}
          >
            <h3 className="article-title">{article.title}</h3>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: index === currentSlide ? `${progress}%` : '0%',
                  backgroundColor: index === currentSlide ? 'white' : 'rgba(255, 255, 255, 0.3)'
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </header>
  );
};

export default Header;
